import { createCachedSelector } from 're-reselect';

import { COMPONENT_SCOPES } from '../../components/panels/panelSchema';

export const findActiveAncestorIds = (comp, comps, isParent = false) => {
  const active = [];
  if (isParent) {
    active.push(comp.id);
  }
  if (comp.parent) {
    const parent = comps.find((c) => c.id === comp.parent);
    if (parent) {
      active.push(...findActiveAncestorIds(parent, comps, true));
    }
  }
  return active;
};

export const findActiveDescendantIds = (comp, node, found = false) => {
  const active = [];
  let add = found;
  if (add) {
    active.push(node.id);
  }
  if (node.id === comp.id) {
    add = true;
  }
  if (node.children) {
    for (const child of node.children) active.push(...findActiveDescendantIds(comp, child, add));
  }
  return active;
};

export const getStatuses = (compStatuses, compId) => {
  const original = compStatuses.find((s) => s.id === compId);

  const errors = compStatuses.filter((s) => s.status === 2);
  const warns = compStatuses.filter((s) => s.status === 1);
  const oks = compStatuses.filter((s) => s.status === 0);
  const riskStatus = compStatuses
    .filter((s) => s.id === compId && s.eventRiskStatus?.length > 0)
    .toSorted((x, y) => y.eventDateTime - x.eventDateTime);

  if (original && original.status === 2) {
    return [
      {
        ...original,
        eventRiskStatus: [],
      },
      {
        ...original,
        errors: errors.length,
        warns: warns.length,
        oks: oks.length,
        eventRiskStatus: riskStatus[0]?.eventRiskStatus,
      },
    ];
  }
  if (errors.length > 0) {
    return [
      original,
      {
        ...errors[0],
        errors: errors.length,
        warns: warns.length,
        oks: oks.length,
      },
    ];
  }

  if (original && original.status === 1) {
    return [
      {
        ...original,
        eventRiskStatus: [],
      },
      {
        ...original,
        warns: warns.length,
        oks: oks.length,
        eventRiskStatus: riskStatus[0]?.eventRiskStatus,
      },
    ];
  }
  if (warns.length > 0) {
    return [
      original,
      {
        ...warns[0],
        warns: warns.length,
        oks: oks.length,
      },
    ];
  }

  if (original && original.status === 0) {
    return [
      {
        ...original,
        eventRiskStatus: [],
      },
      { ...original, oks: oks.length, eventRiskStatus: riskStatus[0]?.eventRiskStatus },
    ];
  }
  if (oks.length > 0) {
    return [original, { ...oks[0], oks: oks.length }];
  }
  if (riskStatus.length > 0) {
    return [original, { ...original, ...riskStatus[0]?.eventRiskStatus }];
  }

  return [undefined, undefined];
};

export const buildComponentsWithStatus = (components = [], healthStatuses = []) => {
  const healthByCompId = {};
  for (const status of healthStatuses) {
    const { id: compId, status: healthStatus } = status;
    if (healthStatus !== null) {
      const comp = components.find((c) => c.id === compId);
      if (comp) {
        const ids = [compId, ...comp.ancestorIds];
        for (const id of ids) {
          healthByCompId[id] = [...(healthByCompId[id] || []), status];
        }
      }
    }
  }
  const mapped = components.map((comp) => {
    const [ownStatus, filteredStatus] = getStatuses(healthByCompId[comp.id] || [], comp.id);
    return {
      ...comp,
      ownStatus,
      filteredStatus,
    };
  });
  return mapped;
};

const getScopeKey = (scope) => {
  const { selectedScope = 'all', activeComponentTypeFilter = 'none' } = scope;
  return `${selectedScope}-${activeComponentTypeFilter}`;
};

// Uses re-reselect's caching strategy for selectors to cache scoped comps
export const getScopedComponents = createCachedSelector(
  (argsObject) => argsObject,
  ({ components, activeComponentId, componentScope }) => {
    const { selectedScope, activeComponentTypeFilter } = componentScope;
    const activeComponent = components.find((c) => c.id === activeComponentId);

    if (activeComponent && COMPONENT_SCOPES.includes(selectedScope) && selectedScope !== 'all') {
      // Does not match filter
      if (
        activeComponentTypeFilter !== 'any' &&
        activeComponent.type !== activeComponentTypeFilter
      ) {
        return [];
      }

      if (selectedScope === 'active_children_all' || selectedScope === 'active_children_none') {
        return [
          activeComponent,
          ...activeComponent.descendantIds.map((childId) =>
            components.find(({ id }) => id === childId)
          ),
        ];
      }
      return [activeComponent];
    }

    if (selectedScope?.includes('all')) {
      if (activeComponentTypeFilter !== 'any') {
        return components.filter((c) => c.type === activeComponentTypeFilter);
      }
      return components;
    }

    if (selectedScope?.includes('none')) {
      return [];
    }

    return components;
  }
)((argsObject) => {
  const { componentsUpdatedAt, activeComponentId, componentScope } = argsObject;
  const scopeKey = getScopeKey(componentScope);
  return `${componentsUpdatedAt}-${activeComponentId || ''}-${scopeKey}`;
});

export const generatePathToId = (tree, id) => {
  let path = [];

  if (!tree.children) return path;

  const iterator = (p) => (object) => {
    if (object.id === id) {
      path = [...p, object.id];
      return true;
    }
    return Array.isArray(object.children) && object.children.some(iterator([...p, object.id]));
  };

  tree.children.some(iterator([]));

  return path;
};
